import { 
  Component, 
  OnInit, 
  OnDestroy, 
  Input, 
  Output, 
  EventEmitter,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  ViewChild,
  ElementRef,
  TemplateRef,
  NgZone,
  Inject,
  PLATFORM_ID,
  HostListener
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Subscription, combineLatest } from 'rxjs';
import { 
  ScreenRecorderService, 
  RecordingState, 
  RecordingConfig, 
  RecordingResult,
  WebcamPosition,
  BrowserCapabilities,
  StreamWarning,
  ScreenRecordingHistoryItem
} from './screen-recorder.service';
import { NativeCapturePermissions } from 'src/app/interfaces/native-host';
import { NativeCaptureSource } from 'src/app/interfaces/native-host';
import { NativeHostService } from 'src/app/services/native-host.service';
import { ThemeService } from 'src/app/services/theme.service';
import { UiPopoverService } from 'src/app/components/elements/ui-popover/ui-popover.service';

@Component({
  selector: 'app-screen-recorder-modal',
  templateUrl: './screen-recorder-modal.component.html',
  styleUrls: ['./screen-recorder-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.theme-light]': 'theme.isLight' }
})
export class ScreenRecorderModalComponent implements OnInit, OnDestroy {
  @Input() userId: string = '';
  @Input() folder: string = 'screen-recordings';
  @Input() title: string = '';
  @Input() defaultWebcamPosition: WebcamPosition = 'bottom-right';
  @Input() defaultWebcamSize: number = 150;
  @Input() defaultCountdown: number = 3;

  @Output() recordingComplete = new EventEmitter<RecordingResult>();
  @Output() modalClosed = new EventEmitter<void>();

  @ViewChild('previewCanvas') previewCanvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('startRecordingBtn') startRecordingBtnRef!: ElementRef<HTMLButtonElement>;
  @ViewChild('historyTrigger', { read: ElementRef }) historyTriggerRef!: ElementRef<HTMLButtonElement>;
  @ViewChild('historyPopoverTemplate') historyPopoverTemplate!: TemplateRef<any>;

  // localStorage keys for settings persistence
  private readonly STORAGE_KEY_COUNTDOWN = 'screenRecorder.countdown';
  private readonly STORAGE_KEY_POSITION = 'screenRecorder.position';
  private readonly STORAGE_KEY_SIZE = 'screenRecorder.size';
  private readonly STORAGE_KEY_BORDER = 'screenRecorder.border';
  private readonly STORAGE_KEY_MICROPHONE = 'screenRecorder.microphone';

  private isBrowser: boolean;

  // State from service
  state: RecordingState = 'idle';
  recordingTime: number = 0;
  uploadProgress: number = 0;
  countdown: number = 0;
  downloadUrl: string | null = null;
  mediaId: string | null = null;
  error: string | null = null;
  isModalVisible: boolean = false;
  isMinimized: boolean = false;
  
  // Stream health
  streamWarning: StreamWarning = 'none';
  actualFps: number = 30;

  // Copy feedback - separate state for each button
  copiedShare: boolean = false;
  copiedDirect: boolean = false;
  copiedEmbed: boolean = false;
  
  // Auto-copy notification
  showAutoCopiedNotification: boolean = false;

  // Link mode toggle (share vs embed)
  linkMode: 'share' | 'embed' = 'share';

  // Media metadata editing
  mediaTitle: string = '';
  mediaDescription: string = '';
  isSavingMetadata: boolean = false;

  // Recording history popover
  isHistoryOpen: boolean = false;
  isLoadingHistory: boolean = false;
  historyError: string | null = null;
  recordingHistory: ScreenRecordingHistoryItem[] = [];
  copiedHistoryShareId: string | null = null;

  // Setup state - independent toggles for each source
  screenStream: MediaStream | null = null;
  webcamStream: MediaStream | null = null;
  audioStream: MediaStream | null = null;
  
  isScreenActive: boolean = false;
  isWebcamActive: boolean = false;
  isAudioActive: boolean = false;
  
  isLoadingScreen: boolean = false;
  isLoadingWebcam: boolean = false;
  isLoadingAudio: boolean = false;

  // Settings
  webcamPosition: WebcamPosition = 'bottom-right';
  webcamSize: number = 150;
  webcamBorderWidth: number = 3;
  countdownSeconds: number = 3;

  // Audio devices
  audioDevices: MediaDeviceInfo[] = [];
  selectedAudioDeviceId: string = '';

  // Browser capabilities
  capabilities: BrowserCapabilities | null = null;
  nativeCapturePermissions: NativeCapturePermissions | null = null;
  nativeCaptureSources: NativeCaptureSource[] = [];
  selectedNativeSourceId: string = '';
  isLoadingNativePermissions: boolean = false;
  isLoadingNativeSources: boolean = false;

  // Preview compositing
  private previewCtx: CanvasRenderingContext2D | null = null;
  private nativePreviewImage: HTMLImageElement | null = null;
  private screenVideo: HTMLVideoElement | null = null;
  private webcamVideo: HTMLVideoElement | null = null;
  private previewAnimationId: number | null = null;

  private subscriptions: Subscription = new Subscription();
  private wasVisible: boolean = false;
  private hasAutoSetupRun: boolean = false;

  get isElectronNativeCapture(): boolean {
    return this.recorderService.isElectronDesktop;
  }

  get canUseWebcamInCurrentMode(): boolean {
    return this.capabilities?.webcam === true;
  }

  constructor(
    public recorderService: ScreenRecorderService,
    public theme: ThemeService,
    private nativeHostService: NativeHostService,
    private popoverService: UiPopoverService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    // Load persisted settings or fall back to defaults
    const validPositions: WebcamPosition[] = ['bottom-right', 'bottom-left', 'top-right', 'top-left'];
    const loadedPosition = this.loadSetting(this.STORAGE_KEY_POSITION, this.defaultWebcamPosition);
    this.webcamPosition = validPositions.includes(loadedPosition as WebcamPosition) 
      ? loadedPosition as WebcamPosition 
      : this.defaultWebcamPosition;
    this.webcamSize = this.loadNumberSetting(this.STORAGE_KEY_SIZE, this.defaultWebcamSize);
    this.webcamBorderWidth = this.loadNumberSetting(this.STORAGE_KEY_BORDER, 3);
    this.countdownSeconds = this.loadNumberSetting(this.STORAGE_KEY_COUNTDOWN, this.defaultCountdown);
    this.selectedAudioDeviceId = this.loadSetting(this.STORAGE_KEY_MICROPHONE, '');

    // Check browser capabilities
    this.capabilities = this.recorderService.checkBrowserCapabilities();
    if (this.isBrowser) {
      void this.refreshNativeCapturePermissions();
      void this.refreshNativeCaptureSources();
    }

    // Load available audio devices
    if (this.isBrowser) {
      this.loadAudioDevices();
    }

    // Subscribe to service state
    this.subscriptions.add(
      combineLatest([
        this.recorderService.state$,
        this.recorderService.recordingTime$,
        this.recorderService.uploadProgress$,
        this.recorderService.countdown$,
        this.recorderService.downloadUrl$,
        this.recorderService.mediaId$,
        this.recorderService.error$,
        this.recorderService.isModalVisible$,
        this.recorderService.isMinimized$,
        this.recorderService.streamWarning$,
        this.recorderService.actualFps$
      ]).subscribe(([state, recordingTime, uploadProgress, countdown, downloadUrl, mediaId, error, isModalVisible, isMinimized, streamWarning, actualFps]) => {
        this.capabilities = this.recorderService.checkBrowserCapabilities();

        // Detect when modal just became visible in idle state
        const justBecameVisible = isModalVisible && !isMinimized && !this.wasVisible;
        const isIdleAndReady = state === 'idle' && !this.hasAutoSetupRun;

        // Detect when recording just completed (state transitioned to 'complete')
        const justCompleted = state === 'complete' && this.state !== 'complete' && mediaId;

        this.state = state;
        this.recordingTime = recordingTime;
        this.uploadProgress = uploadProgress;
        this.countdown = countdown;
        this.downloadUrl = downloadUrl;
        this.mediaId = mediaId;
        this.error = error;
        this.isModalVisible = isModalVisible;
        this.isMinimized = isMinimized;
        this.streamWarning = streamWarning;
        this.actualFps = actualFps;

        // Track visibility
        this.wasVisible = isModalVisible && !isMinimized;

        // Auto-setup when modal becomes visible in idle state
        if (justBecameVisible && isIdleAndReady) {
          this.hasAutoSetupRun = true;
          this.autoSetupMediaSources();
        }

        // Auto-copy share URL when recording completes
        if (justCompleted) {
          this.autoCopyShareUrl();
        }

        // Reset auto-setup flag when recording completes or modal closes
        if (state === 'complete' || state === 'error' || !isModalVisible) {
          this.hasAutoSetupRun = false;
        }

        this.cdr.markForCheck();
      })
    );

    this.subscriptions.add(
      this.recorderService.nativeDesktopAvailable$.subscribe(() => {
        this.capabilities = this.recorderService.checkBrowserCapabilities();
        void this.refreshNativeCapturePermissions();
        void this.refreshNativeCaptureSources();
        this.cdr.markForCheck();
      })
    );
  }

  ngOnDestroy(): void {
    this.closeRecordingHistory();
    this.subscriptions.unsubscribe();
    this.stopAllPreviews();
  }

  /**
   * Open the modal and auto-start media capture sequence
   */
  open(): void {
    this.hasAutoSetupRun = false; // Reset so auto-setup can run
    this.recorderService.openModal();
    this.recorderService.reset();
    void this.refreshNativeCapturePermissions();
    void this.refreshNativeCaptureSources();

    if (this.isElectronNativeCapture && this.state === 'idle') {
      this.hasAutoSetupRun = true;
      void this.autoSetupMediaSources(false);
    }
  }

  /**
   * Auto-setup media sources in sequence: mic -> webcam -> screen
   * This provides a smoother onboarding experience with visible delays
   */
  private async autoSetupMediaSources(waitForRender: boolean = true): Promise<void> {
    // Small delay to let modal render
    if (waitForRender) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    if (this.isElectronNativeCapture) {
      await this.refreshNativeCapturePermissions();
      await this.refreshNativeCaptureSources();
      this.isScreenActive = this.nativeCapturePermissions?.canRecord === true && !!this.selectedNativeSourceId;
      this.isLoadingScreen = false;
      this.cdr.markForCheck();

      if (this.capabilities?.audio && !this.isAudioActive) {
        try {
          await this.startAudioPreview();
        } catch (e) {
          console.warn('Auto-setup: Could not start microphone', e);
        }
      }

      if (this.capabilities?.webcam && !this.isWebcamActive) {
        try {
          await this.startWebcamPreview();
        } catch (e) {
          console.warn('Auto-setup: Could not start webcam', e);
        }
      }

      this.refocusAfterScreenPicker();
      return;
    }

    // Step 1: Start microphone (if available)
    if (this.capabilities?.audio && !this.isAudioActive) {
      try {
        await this.startAudioPreview();
      } catch (e) {
        console.warn('Auto-setup: Could not start microphone', e);
      }
    }

    // Delay so user can see mic activate
    await new Promise(resolve => setTimeout(resolve, 500));

    // Step 2: Start webcam (if available)
    if (this.capabilities?.webcam && !this.isWebcamActive) {
      try {
        await this.startWebcamPreview();
      } catch (e) {
        console.warn('Auto-setup: Could not start webcam', e);
      }
    }

    // Delay so user can see webcam activate
    await new Promise(resolve => setTimeout(resolve, 500));

    // Step 3: Start screen capture (always prompts user to select)
    if (!this.isScreenActive) {
      try {
        await this.startScreenPreview();
        // After screen picker closes, the browser window often loses focus
        // We need to aggressively try to refocus the window and button
        this.refocusAfterScreenPicker();
      } catch (e) {
        console.warn('Auto-setup: Could not start screen capture', e);
      }
    }
  }
  
  /**
   * Aggressively try to refocus the window and start button after screen picker closes
   * The native screen picker often causes the browser window to lose focus
   */
  private refocusAfterScreenPicker(): void {
    if (!this.isBrowser) return;
    
    // Strategy 1: Immediate focus attempts
    window.focus();
    this.focusStartButton();
    
    // Strategy 2: Delayed attempts (browser might need time to process)
    const delays = [50, 100, 200, 500];
    delays.forEach(delay => {
      setTimeout(() => {
        window.focus();
        this.focusStartButton();
      }, delay);
    });
    
    // Strategy 3: Listen for when the window regains focus
    const onWindowFocus = () => {
      this.focusStartButton();
      // Remove listener after a few seconds to avoid memory leaks
      setTimeout(() => {
        window.removeEventListener('focus', onWindowFocus);
      }, 5000);
    };
    window.addEventListener('focus', onWindowFocus);
    
    // Strategy 4: Use visibility change as backup
    const onVisibilityChange = () => {
      if (!document.hidden) {
        window.focus();
        this.focusStartButton();
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    
    // Clean up visibility listener after timeout
    setTimeout(() => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }, 5000);
  }
  
  /**
   * Focus the start recording button if it exists and is enabled
   */
  private focusStartButton(): void {
    if (this.startRecordingBtnRef?.nativeElement && !this.startRecordingBtnRef.nativeElement.disabled) {
      this.startRecordingBtnRef.nativeElement.focus();
    }
  }

  /**
   * Close the modal
   */
  close(): void {
    this.closeRecordingHistory();
    this.stopAllPreviews();
    this.recorderService.closeModal();
    this.modalClosed.emit();
  }

  /**
   * Minimize the modal
   */
  minimize(): void {
    this.recorderService.minimizeModal();
  }

  /**
   * Restore from minimized state
   */
  restore(): void {
    this.recorderService.restoreModal();
  }

  // ============ Screen Capture ============

  async toggleScreen(): Promise<void> {
    if (this.isElectronNativeCapture) {
      await this.startScreenPreview();
      return;
    }

    if (this.isScreenActive) {
      this.stopScreenPreview();
    } else {
      await this.startScreenPreview();
    }
  }

  async startScreenPreview(): Promise<void> {
    if (this.screenStream) return;

    if (this.isElectronNativeCapture) {
      await this.refreshNativeCapturePermissions();
      await this.refreshNativeCaptureSources();
      this.isScreenActive = this.nativeCapturePermissions?.canRecord === true && !!this.selectedNativeSourceId;
      this.error = this.nativeCapturePermissions?.canRecord === false
        ? this.nativeCapturePermissions.message
        : !this.selectedNativeSourceId
          ? 'Choose which display to record before starting.'
        : null;
      this.cdr.markForCheck();
      return;
    }
    
    this.isLoadingScreen = true;
    this.cdr.markForCheck();
    
    try {
      this.screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          frameRate: { ideal: 30 }
        },
        audio: false
      });

      // Handle user stopping screen share via browser UI
      this.screenStream.getVideoTracks()[0].onended = () => {
        this.ngZone.run(() => {
          this.stopScreenPreview();
        });
      };

      // Create video element for preview
      this.screenVideo = document.createElement('video');
      this.screenVideo.srcObject = this.screenStream;
      this.screenVideo.muted = true;
      await this.screenVideo.play();

      this.isScreenActive = true;
      this.isLoadingScreen = false;
      this.cdr.markForCheck();

      // Start preview loop
      this.startPreviewLoop();
    } catch (e: any) {
      console.warn('Could not start screen capture:', e);
      this.isLoadingScreen = false;
      if (e.name !== 'NotAllowedError') {
        this.error = 'Failed to start screen capture. Please try again.';
      }
      this.cdr.markForCheck();
    }
  }

  stopScreenPreview(): void {
    if (this.isElectronNativeCapture) {
      this.isScreenActive = false;
      this.cdr.markForCheck();
      return;
    }

    if (this.screenStream) {
      this.screenStream.getTracks().forEach(track => track.stop());
      this.screenStream = null;
    }
    if (this.screenVideo) {
      this.screenVideo.pause();
      this.screenVideo.srcObject = null;
      this.screenVideo = null;
    }
    this.isScreenActive = false;
    this.stopPreviewLoop();
    this.cdr.markForCheck();
  }

  // ============ Webcam ============

  async toggleWebcam(): Promise<void> {
    if (this.isWebcamActive) {
      this.stopWebcamPreview();
    } else {
      await this.startWebcamPreview();
    }
  }

  async startWebcamPreview(): Promise<void> {
    if (this.webcamStream || !this.capabilities?.webcam) return;
    
    this.isLoadingWebcam = true;
    this.cdr.markForCheck();
    
    try {
      this.webcamStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 320 },
          height: { ideal: 320 },
          facingMode: 'user'
        },
        audio: false
      });

      // Create video element for preview
      this.webcamVideo = document.createElement('video');
      this.webcamVideo.srcObject = this.webcamStream;
      this.webcamVideo.muted = true;
      await this.webcamVideo.play();

      this.isWebcamActive = true;
      this.isLoadingWebcam = false;
      this.cdr.markForCheck();

      // Start preview loop (works with or without screen share)
      this.startPreviewLoop();
    } catch (e) {
      console.warn('Could not start webcam:', e);
      this.isLoadingWebcam = false;
      this.error = 'Could not access webcam. Please check permissions.';
      this.cdr.markForCheck();
    }
  }

  stopWebcamPreview(): void {
    if (this.webcamStream) {
      this.webcamStream.getTracks().forEach(track => track.stop());
      this.webcamStream = null;
    }
    if (this.webcamVideo) {
      this.webcamVideo.pause();
      this.webcamVideo.srcObject = null;
      this.webcamVideo = null;
    }
    this.isWebcamActive = false;

    if (this.isElectronNativeCapture) {
      this.stopPreviewLoop();
    }

    this.cdr.markForCheck();
  }

  // ============ Audio ============

  async toggleAudio(): Promise<void> {
    if (this.isAudioActive) {
      this.stopAudioPreview();
    } else {
      await this.startAudioPreview();
    }
  }

  async startAudioPreview(): Promise<void> {
    if (this.audioStream || !this.capabilities?.audio) return;
    
    this.isLoadingAudio = true;
    this.cdr.markForCheck();
    
    try {
      const audioConstraints: MediaTrackConstraints = {
        echoCancellation: true,
        noiseSuppression: true,
        sampleRate: 44100
      };
      
      // Use selected device if available
      if (this.selectedAudioDeviceId) {
        audioConstraints.deviceId = { exact: this.selectedAudioDeviceId };
      }
      
      this.audioStream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints,
        video: false
      });

      // Refresh device list to get labels (after permission granted)
      await this.loadAudioDevices();
      
      // Update selected device ID if it changed
      const audioTrack = this.audioStream.getAudioTracks()[0];
      if (audioTrack) {
        const settings = audioTrack.getSettings();
        if (settings.deviceId) {
          this.selectedAudioDeviceId = settings.deviceId;
        }
      }

      this.isAudioActive = true;
      this.isLoadingAudio = false;
      this.cdr.markForCheck();
    } catch (e) {
      console.warn('Could not access microphone:', e);
      this.isLoadingAudio = false;
      this.error = 'Could not access microphone. Please check permissions.';
      this.cdr.markForCheck();
    }
  }

  stopAudioPreview(): void {
    if (this.audioStream) {
      this.audioStream.getTracks().forEach(track => track.stop());
      this.audioStream = null;
    }
    this.isAudioActive = false;
    this.cdr.markForCheck();
  }

  // ============ Audio Device Management ============

  async loadAudioDevices(): Promise<void> {
    if (!this.isBrowser || !navigator?.mediaDevices?.enumerateDevices) {
      return;
    }

    try {
      // Need to request permission first to get device labels
      const devices = await navigator.mediaDevices.enumerateDevices();
      this.audioDevices = devices.filter(d => d.kind === 'audioinput');
      
      // Set default device if not already set
      if (!this.selectedAudioDeviceId && this.audioDevices.length > 0) {
        this.selectedAudioDeviceId = this.audioDevices[0].deviceId;
      }
      this.cdr.markForCheck();
    } catch (e) {
      console.warn('Could not enumerate audio devices:', e);
    }
  }

  async onAudioDeviceChange(deviceId: string): Promise<void> {
    this.selectedAudioDeviceId = deviceId;
    this.saveSetting(this.STORAGE_KEY_MICROPHONE, deviceId);
    
    // If audio is active, switch to the new device
    if (this.isAudioActive && this.audioStream) {
      this.audioStream.getTracks().forEach(track => track.stop());
      this.audioStream = null;
      
      try {
        this.audioStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            deviceId: { exact: deviceId },
            echoCancellation: true,
            noiseSuppression: true,
            sampleRate: 44100
          },
          video: false
        });
        this.cdr.markForCheck();
      } catch (e) {
        console.warn('Could not switch to audio device:', e);
        this.error = 'Could not switch microphone. Please try again.';
        this.isAudioActive = false;
        this.cdr.markForCheck();
      }
    }
  }

  getAudioDeviceLabel(device: MediaDeviceInfo): string {
    return device.label || `Microphone ${this.audioDevices.indexOf(device) + 1}`;
  }

  // ============ Preview Loop ============

  private startPreviewLoop(): void {
    if (this.previewAnimationId) return;

    // Initialize canvas
    const canvas = this.previewCanvasRef?.nativeElement;
    if (!canvas) return;

    this.previewCtx = canvas.getContext('2d');
    if (!this.previewCtx) return;

    // Set canvas size based on screen capture, or default for webcam-only
    if (this.screenVideo && this.screenStream) {
      const settings = this.screenStream.getVideoTracks()[0]?.getSettings();
      canvas.width = settings?.width || 1920;
      canvas.height = settings?.height || 1080;
    } else if (this.isElectronNativeCapture) {
      this.syncNativePreviewImage();
      canvas.width = this.nativePreviewImage?.naturalWidth || 1920;
      canvas.height = this.nativePreviewImage?.naturalHeight || 1080;
    } else {
      // Default 16:9 canvas for webcam-only preview
      canvas.width = 1280;
      canvas.height = 720;
    }

    this.ngZone.runOutsideAngular(() => {
      this.drawPreviewFrame();
    });
  }

  private drawPreviewFrame(): void {
    if (!this.previewCtx || !this.previewCanvasRef?.nativeElement) {
      this.previewAnimationId = null;
      return;
    }

    const canvas = this.previewCanvasRef.nativeElement;
    const ctx = this.previewCtx;

    // Clear canvas
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw screen capture or the selected native display preview
    if (this.screenVideo && this.isScreenActive) {
      ctx.drawImage(this.screenVideo, 0, 0, canvas.width, canvas.height);
    } else if (this.isElectronNativeCapture && this.selectedNativeSource?.thumbnailDataUrl) {
      this.syncNativePreviewImage();

      if (this.nativePreviewImage?.complete) {
        ctx.drawImage(this.nativePreviewImage, 0, 0, canvas.width, canvas.height);
      }
    }

    // Draw webcam bubble
    if (this.webcamVideo && this.isWebcamActive) {
      const bubbleSize = this.webcamSize;
      const padding = 20;
      let x = padding;
      let y = canvas.height - bubbleSize - padding;

      switch (this.webcamPosition) {
        case 'bottom-right':
          x = canvas.width - bubbleSize - padding;
          y = canvas.height - bubbleSize - padding;
          break;
        case 'top-left':
          x = padding;
          y = padding;
          break;
        case 'top-right':
          x = canvas.width - bubbleSize - padding;
          y = padding;
          break;
        case 'bottom-left':
          x = padding;
          y = canvas.height - bubbleSize - padding;
          break;
        default:
          // Default to bottom-right
          x = canvas.width - bubbleSize - padding;
          y = canvas.height - bubbleSize - padding;
          break;
      }

      // Draw circular webcam bubble with border
      ctx.save();
      ctx.beginPath();
      ctx.arc(x + bubbleSize / 2, y + bubbleSize / 2, bubbleSize / 2, 0, Math.PI * 2);
      ctx.clip();
      
      // Draw webcam video (maintaining aspect ratio, centered)
      const videoWidth = this.webcamVideo.videoWidth || 320;
      const videoHeight = this.webcamVideo.videoHeight || 320;
      const scale = Math.max(bubbleSize / videoWidth, bubbleSize / videoHeight);
      const scaledWidth = videoWidth * scale;
      const scaledHeight = videoHeight * scale;
      const offsetX = x + (bubbleSize - scaledWidth) / 2;
      const offsetY = y + (bubbleSize - scaledHeight) / 2;
      
      ctx.drawImage(this.webcamVideo, offsetX, offsetY, scaledWidth, scaledHeight);
      ctx.restore();

      // Draw border around bubble (only if borderWidth > 0)
      if (this.webcamBorderWidth > 0) {
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = this.webcamBorderWidth;
        ctx.beginPath();
        ctx.arc(x + bubbleSize / 2, y + bubbleSize / 2, bubbleSize / 2 - (this.webcamBorderWidth / 2), 0, Math.PI * 2);
        ctx.stroke();
      }
    }

    // Continue loop while the browser preview is live, or while Electron needs the webcam overlay compositor.
    if ((!this.isElectronNativeCapture && this.isScreenActive) || this.isWebcamActive) {
      this.previewAnimationId = requestAnimationFrame(() => this.drawPreviewFrame());
    } else {
      this.previewAnimationId = null;
    }
  }

  private stopPreviewLoop(): void {
    if (this.previewAnimationId) {
      cancelAnimationFrame(this.previewAnimationId);
      this.previewAnimationId = null;
    }
  }

  // ============ Stop All ============

  stopAllPreviews(): void {
    this.stopPreviewLoop();
    this.stopScreenPreview();
    this.stopWebcamPreview();
    this.stopAudioPreview();
  }

  // ============ Settings Changes ============

  onPositionChange(position: WebcamPosition): void {
    console.log('Position changed to:', position);
    this.webcamPosition = position;
    this.saveSetting(this.STORAGE_KEY_POSITION, position);
    // Force redraw by restarting preview loop if needed
    if (this.isWebcamActive && !this.previewAnimationId) {
      this.startPreviewLoop();
    }
    this.cdr.markForCheck();
  }

  onSizeChange(size: number): void {
    this.webcamSize = size;
    this.saveNumberSetting(this.STORAGE_KEY_SIZE, size);
  }

  onBorderWidthChange(width: number): void {
    this.webcamBorderWidth = width;
    this.saveNumberSetting(this.STORAGE_KEY_BORDER, width);
  }

  onCountdownChange(seconds: number): void {
    this.countdownSeconds = seconds;
    this.saveNumberSetting(this.STORAGE_KEY_COUNTDOWN, seconds);
  }

  // ============ localStorage Helpers ============

  private loadSetting(key: string, defaultValue: string): string {
    if (!this.isBrowser) return defaultValue;
    const value = localStorage.getItem(key);
    return value !== null ? value : defaultValue;
  }

  private saveSetting(key: string, value: string): void {
    if (!this.isBrowser) return;
    localStorage.setItem(key, value);
  }

  private loadNumberSetting(key: string, defaultValue: number): number {
    if (!this.isBrowser) return defaultValue;
    const value = localStorage.getItem(key);
    return value !== null ? parseFloat(value) : defaultValue;
  }

  private saveNumberSetting(key: string, value: number): void {
    if (!this.isBrowser) return;
    localStorage.setItem(key, value.toString());
  }

  // ============ Recording ============

  async startRecording(): Promise<void> {
    if (!this.userId) {
      this.error = 'User ID is required to save recordings.';
      this.cdr.markForCheck();
      return;
    }

    if (!this.isScreenActive) {
      this.error = 'Please start screen capture first.';
      this.cdr.markForCheck();
      return;
    }

    if (this.isElectronNativeCapture) {
      await this.refreshNativeCapturePermissions();
      if (this.nativeCapturePermissions && !this.nativeCapturePermissions.canRecord) {
        this.error = this.nativeCapturePermissions.message;
        this.cdr.markForCheck();
        return;
      }
    }

    // Stop preview loop - recording will use its own
    this.stopPreviewLoop();

    const config: RecordingConfig = {
      captureScreen: true,
      captureWebcam: this.isWebcamActive,
      captureAudio: this.isAudioActive,
      webcamPosition: this.webcamPosition,
      webcamSize: this.webcamSize,
      webcamBorderWidth: this.webcamBorderWidth,
      countdownSeconds: this.countdownSeconds,
      userId: this.userId,
      folder: this.folder,
      nativeSourceId: this.selectedNativeSourceId || undefined,
      title: this.title || `Recording ${new Date().toLocaleDateString()}`,
      audioDeviceId: this.selectedAudioDeviceId || undefined
    };

    // Pass existing streams to service
    await this.recorderService.startRecordingWithStreams(
      config,
      this.screenStream,
      this.webcamStream,
      this.audioStream,
      (result) => {
        this.recordingComplete.emit(result);
      }
    );

    // Detach preview video elements without stopping tracks; service now owns the streams.
    if (this.screenVideo) {
      this.screenVideo.pause();
      this.screenVideo.srcObject = null;
    }
    if (this.webcamVideo) {
      this.webcamVideo.pause();
      this.webcamVideo.srcObject = null;
    }

    // Clear local references - service now owns the streams
    this.screenStream = null;
    this.webcamStream = null;
    this.audioStream = null;
    this.screenVideo = null;
    this.webcamVideo = null;
    this.isScreenActive = false;
    this.isWebcamActive = false;
    this.isAudioActive = false;
  }

  /**
   * Stop recording
   */
  stopRecording(): void {
    this.recorderService.stopRecording();
  }
  
  /**
   * Discard recording without saving
   */
  discardRecording(): void {
    this.recorderService.discardRecording();
  }

  /**
   * Cancel recording
   */
  cancelRecording(): void {
    this.recorderService.cancelRecording();
    this.close();
  }

  /**
   * Copy download URL to clipboard
   */
  async copyUrl(): Promise<void> {
    await this.recorderService.copyToClipboard();
  }

  /**
   * Get the share page URL
   */
  get sharePageUrl(): string | null {
    if (!this.mediaId || !this.isBrowser) return null;
    const baseUrl = window.location.origin;
    return `${baseUrl}/file/${this.mediaId}`;
  }

  /**
   * Copy share page URL to clipboard
   */
  async copySharePageUrl(): Promise<void> {
    const url = this.sharePageUrl;
    if (url && this.isBrowser) {
      await navigator.clipboard.writeText(url);
      this.copiedShare = true;
      this.cdr.markForCheck();
      
      // Reset after 2 seconds
      setTimeout(() => {
        this.copiedShare = false;
        this.cdr.markForCheck();
      }, 2000);
    }
  }

  /**
   * Auto-copy share URL when recording completes
   */
  private async autoCopyShareUrl(): Promise<void> {
    const url = this.sharePageUrl;
    if (url && this.isBrowser) {
      try {
        await navigator.clipboard.writeText(url);
        this.showAutoCopiedNotification = true;
        this.cdr.markForCheck();
        
        // Hide notification after 3 seconds
        setTimeout(() => {
          this.showAutoCopiedNotification = false;
          this.cdr.markForCheck();
        }, 3000);
      } catch (err) {
        console.warn('Auto-copy to clipboard failed:', err);
      }
    }
  }

  /**
   * Get embed code for responsive iframe (16:9 aspect ratio)
   */
  get embedCode(): string {
    if (!this.downloadUrl) return '';
    return `<div style="position:relative;padding-bottom:56.25%;height:0;overflow:hidden;"><iframe src="${this.downloadUrl}" style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;" allowfullscreen></iframe></div>`;
  }

  /**
   * Copy direct URL to clipboard
   */
  async copyDirectUrl(): Promise<void> {
    if (this.downloadUrl && this.isBrowser) {
      await navigator.clipboard.writeText(this.downloadUrl);
      this.copiedDirect = true;
      this.cdr.markForCheck();
      
      setTimeout(() => {
        this.copiedDirect = false;
        this.cdr.markForCheck();
      }, 2000);
    }
  }

  /**
   * Copy embed code to clipboard
   */
  async copyEmbedCode(): Promise<void> {
    const code = this.embedCode;
    if (code && this.isBrowser) {
      await navigator.clipboard.writeText(code);
      this.copiedEmbed = true;
      this.cdr.markForCheck();
      
      setTimeout(() => {
        this.copiedEmbed = false;
        this.cdr.markForCheck();
      }, 2000);
    }
  }

  async toggleRecordingHistory(event?: MouseEvent): Promise<void> {
    event?.stopPropagation();

    if (this.isHistoryOpen) {
      this.closeRecordingHistory();
      return;
    }

    if (!this.historyTriggerRef?.nativeElement || !this.historyPopoverTemplate) {
      return;
    }

    this.isHistoryOpen = true;
    this.popoverService.open({
      element: this.historyTriggerRef.nativeElement,
      placement: 'top-left',
      align: 'start',
      offset: 8,
      dimensions: {
        width: 520,
        maxWidth: 'calc(100vw - 16px)',
        maxHeight: 'min(460px, calc(100vh - 32px))'
      },
      title: 'Recording History',
      subtitle: 'Saved screen videos',
      bodyTemplate: this.historyPopoverTemplate,
      noPadding: true,
      backdrop: true,
      backdropDismiss: true,
      displayMode: this.theme.isLight ? 'light' : 'dark',
      onClose: () => {
        this.isHistoryOpen = false;
        this.cdr.markForCheck();
      }
    });
    this.cdr.markForCheck();

    await this.loadRecordingHistory(true);
  }

  closeRecordingHistory(): void {
    if (!this.isHistoryOpen) {
      return;
    }

    this.isHistoryOpen = false;
    this.popoverService.close();
    this.cdr.markForCheck();
  }

  private async loadRecordingHistory(forceRefresh: boolean = false): Promise<void> {
    if (!this.userId) {
      this.recordingHistory = [];
      this.historyError = 'Sign in before viewing recording history.';
      this.cdr.markForCheck();
      return;
    }

    if (this.recordingHistory.length > 0 && !forceRefresh) {
      return;
    }

    this.isLoadingHistory = true;
    this.historyError = null;
    this.cdr.markForCheck();

    try {
      this.recordingHistory = await this.recorderService.getRecordingHistory(this.userId);
    } catch (error) {
      console.error('Failed to load recording history:', error);
      this.historyError = 'Could not load recording history.';
    } finally {
      this.isLoadingHistory = false;
      this.cdr.markForCheck();
    }
  }

  getHistoryShareUrl(recording: ScreenRecordingHistoryItem): string {
    if (!this.isBrowser) {
      return `/file/${recording.id}`;
    }

    return `${window.location.origin}/file/${recording.id}`;
  }

  async copyHistoryShareUrl(recording: ScreenRecordingHistoryItem, event?: MouseEvent): Promise<void> {
    event?.stopPropagation();
    if (!this.isBrowser) {
      return;
    }

    await navigator.clipboard.writeText(this.getHistoryShareUrl(recording));
    this.copiedHistoryShareId = recording.id;
    this.cdr.markForCheck();

    setTimeout(() => {
      this.copiedHistoryShareId = null;
      this.cdr.markForCheck();
    }, 2000);
  }

  formatHistoryDate(recording: ScreenRecordingHistoryItem): string {
    if (!recording.createdAt) {
      return 'Unknown date';
    }

    return recording.createdAt.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  formatHistoryDuration(recording: ScreenRecordingHistoryItem): string {
    if (!recording.duration) {
      return 'Video';
    }

    return this.formatTime(recording.duration);
  }

  formatHistorySize(recording: ScreenRecordingHistoryItem): string | null {
    if (!recording.size) {
      return null;
    }

    if (recording.size < 1024 * 1024) {
      return `${Math.round(recording.size / 1024)} KB`;
    }

    return `${(recording.size / (1024 * 1024)).toFixed(1)} MB`;
  }

  /**
   * Save media metadata (title/description)
   */
  async saveMediaMetadata(): Promise<void> {
    if (!this.mediaId || this.isSavingMetadata) return;
    
    this.isSavingMetadata = true;
    this.cdr.markForCheck();
    
    try {
      await this.recorderService.updateMediaMetadata(this.mediaId, {
        title: this.mediaTitle.trim(),
        description: this.mediaDescription.trim()
      });
    } catch (err) {
      console.error('Failed to save metadata:', err);
    } finally {
      this.isSavingMetadata = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * Start a new recording
   */
  startNewRecording(): void {
    // Reset metadata fields
    this.mediaTitle = '';
    this.mediaDescription = '';
    this.copiedShare = false;
    this.copiedDirect = false;
    this.copiedEmbed = false;
    this.closeRecordingHistory();
    this.linkMode = 'share';
    this.recorderService.reset();
    this.error = null;
    
    // Re-trigger auto-setup for the new recording
    this.hasAutoSetupRun = false;
    this.autoSetupMediaSources();
    
    this.cdr.markForCheck();
  }

  /**
   * Format recording time for display
   */
  formatTime(seconds: number): string {
    return this.recorderService.formatTime(seconds);
  }
  
  /**
   * Check if we have a warning to show
   */
  get hasWarning(): boolean {
    return this.streamWarning !== 'none';
  }
  
  /**
   * Get warning message for display
   */
  get warningMessage(): string {
    switch (this.streamWarning) {
      case 'screen-frozen': return 'Screen appears frozen - attempting to recover...';
      case 'webcam-frozen': return 'Webcam appears frozen';
      case 'audio-lost': return 'Audio track lost';
      case 'low-framerate': return `Low framerate detected (${this.actualFps} fps)`;
      default: return '';
    }
  }

  /**
   * Handle backdrop click
   */
  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget && this.state === 'idle') {
      this.close();
    }
  }

  /**
   * Handle Escape key to close modal
   */
  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (this.isModalVisible && !this.isMinimized) {
      event.preventDefault();

      if (this.isHistoryOpen) {
        this.closeRecordingHistory();
        return;
      }

      this.close();
    }
  }

  /**
   * Get position options for webcam dropdown
   */
  get positionOptions(): { value: WebcamPosition; label: string }[] {
    return [
      { value: 'bottom-right', label: 'Bottom Right' },
      { value: 'bottom-left', label: 'Bottom Left' },
      { value: 'top-right', label: 'Top Right' },
      { value: 'top-left', label: 'Top Left' }
    ];
  }

  /**
   * Check if we can start recording
   */
  get canStartRecording(): boolean {
    return this.capabilities?.isFullySupported === true && 
           this.state === 'idle' && 
           !this.isLoadingScreen &&
           !this.isLoadingAudio &&
           !this.isLoadingWebcam &&
           !this.isLoadingNativePermissions &&
           !this.isLoadingNativeSources &&
           this.isScreenActive &&
           (!this.isElectronNativeCapture || this.nativeCapturePermissions?.canRecord === true) &&
           (!this.isElectronNativeCapture || !!this.selectedNativeSourceId) &&
           !!this.userId;
  }

  get showNativeRecorderPanel(): boolean {
    return this.isElectronNativeCapture && (this.isLoadingNativePermissions || this.needsNativePermissionAttention);
  }

  get selectedNativeSource(): NativeCaptureSource | null {
    return this.nativeCaptureSources.find((source) => source.id === this.selectedNativeSourceId) || null;
  }

  async selectNativeSource(sourceId: string): Promise<void> {
    this.selectedNativeSourceId = sourceId;
    this.isScreenActive = !!sourceId && this.nativeCapturePermissions?.canRecord === true;
    this.error = null;
    this.syncNativePreviewImage();

    if (sourceId) {
      await this.nativeHostService.setCaptureSource(sourceId);
    }

    if (this.isWebcamActive && !this.previewAnimationId) {
      this.startPreviewLoop();
    }

    this.cdr.markForCheck();
  }

  get showNativePermissionGuidance(): boolean {
    return this.isElectronNativeCapture && !!this.nativeCapturePermissions;
  }

  get needsNativePermissionAttention(): boolean {
    return this.showNativePermissionGuidance && this.nativeCapturePermissions?.canRecord === false;
  }

  get shouldShowNativeAccessRequest(): boolean {
    return this.showNativePermissionGuidance && this.nativeCapturePermissions?.requestAccessAvailable === true;
  }

  async openNativeCaptureSettings(kind: 'microphone' | 'screen' = 'screen'): Promise<void> {
    const opened = await this.nativeHostService.openCaptureSettings(kind);
    if (!opened) {
      this.error = 'Platfio could not open the system settings automatically. Follow the steps below instead.';
      this.cdr.markForCheck();
      return;
    }

    setTimeout(() => {
      void this.refreshNativeCapturePermissions();
    }, 1500);
  }

  async requestNativeCaptureAccess(): Promise<void> {
    const result = await this.nativeHostService.requestCaptureAccess();

    if (!result.ok) {
      this.error = result.message || 'Platfio could not request native screen access.';
      this.cdr.markForCheck();
    }

    setTimeout(() => {
      void this.refreshNativeCapturePermissions();
    }, 1000);
  }

  async refreshNativeCapturePermissions(): Promise<void> {
    if (!this.isElectronNativeCapture) {
      this.nativeCapturePermissions = null;
      return;
    }

    this.isLoadingNativePermissions = true;
    this.cdr.markForCheck();

    try {
      this.nativeCapturePermissions = await this.nativeHostService.getCapturePermissions();

      if (this.nativeCapturePermissions?.canRecord === false) {
        this.isScreenActive = false;
      }
    } finally {
      this.isLoadingNativePermissions = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * Get status text for current state
   */
  get statusText(): string {
    switch (this.state) {
      case 'countdown': return `Starting in ${this.countdown}...`;
      case 'recording': return `Recording ${this.formatTime(this.recordingTime)}`;
      case 'processing': return 'Processing recording...';
      case 'uploading': return `Uploading ${this.uploadProgress}%`;
      case 'complete': return 'Recording saved!';
      case 'error': return this.error || 'An error occurred';
      default: return 'Ready to record';
    }
  }

  async refreshNativeCaptureSources(): Promise<void> {
    if (!this.isElectronNativeCapture) {
      this.nativeCaptureSources = [];
      this.selectedNativeSourceId = '';
      this.nativePreviewImage = null;
      return;
    }

    this.isLoadingNativeSources = true;
    this.cdr.markForCheck();

    try {
      const sources = await this.nativeHostService.getCaptureSources();
      this.nativeCaptureSources = sources;

      if (!this.selectedNativeSourceId || !sources.some((source) => source.id === this.selectedNativeSourceId)) {
        this.selectedNativeSourceId = sources[0]?.id || '';
      }

      this.isScreenActive = !!this.selectedNativeSourceId && this.nativeCapturePermissions?.canRecord === true;
      this.syncNativePreviewImage();

      if (this.selectedNativeSourceId) {
        await this.nativeHostService.setCaptureSource(this.selectedNativeSourceId);
      }

      if (this.isWebcamActive && !this.previewAnimationId) {
        this.startPreviewLoop();
      }
    } finally {
      this.isLoadingNativeSources = false;
      this.cdr.markForCheck();
    }
  }

  private syncNativePreviewImage(): void {
    const thumbnailDataUrl = this.selectedNativeSource?.thumbnailDataUrl;

    if (!thumbnailDataUrl) {
      this.nativePreviewImage = null;
      return;
    }

    if (this.nativePreviewImage?.src === thumbnailDataUrl) {
      return;
    }

    const image = new Image();
    image.src = thumbnailDataUrl;
    this.nativePreviewImage = image;
  }
}
